import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import toast from "components/partials/toast/toast";

import { useCurrentClient } from "hooks/api/use-clients";
import {
  useGetTouchpointContentTypesQuery,
  useClassTypeOptions,
  usePopulationTypeOptions,
} from "hooks/api/dictionary";

import { getCampaign } from "generated/api/campaign";
import { getProgramTemplates } from "generated/api/program-templates";
import type {
  CampaignDTO,
  CreateTouchpointFromTemplateItemRequest,
  CreateTouchpointFromTemplateItemRequestContentType,
  CreateTouchpointFromTemplateItemRequestPopulationType,
} from "generated/schemas";

import { getErrorMessage, getActionTouchpointTypeLabel } from "utilities";
import { getTouchpointPaths } from "utilities/app-routes";
import {
  formatTouchpointClassesForApi,
  filterTouchpointClassesByAllowedTypes,
} from "components/modals/creative-modal/creative-modal-utils";

import { CreatableTouchpointType, TouchpointType } from "types/touchpoint";

import { LoadingButtonState } from "ui/button";
import {
  CreatedTouchpointSummary,
  CreateTouchpointFromProgramTemplateFormData,
  CreateTouchpointFromProgramTemplateItem,
  CreateTouchpointFromProgramTemplateModalProps,
} from "components/modals/create-touchpoint-from-program-template-modal/create-touchpoint-from-program-template-modal.types";
import {
  getCreateTouchpointFromProgramTemplateSchema,
  getCreateTouchpointFromProgramTemplateValidationSchema,
} from "components/modals/create-touchpoint-from-program-template-modal/create-touchpoint-from-program-template-modal.consts";
import { OptionType } from "ui/select";

const DEFAULT_VALUES: CreateTouchpointFromProgramTemplateItem["formData"] = {
  campaignId: "",
  name: "",
  populationType: "",
  contentType: "",
  copyCode: "",
  classes: [],
  insightsActivator: true,
};

export const useCreateTouchpointFromProgramTemplate = ({
  isOpen,
  onClose,
  onSuccess,
  selectedTemplates,
}: CreateTouchpointFromProgramTemplateModalProps) => {
  const currentClient = useCurrentClient();
  const isMulti = selectedTemplates.length > 1;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState<CreateTouchpointFromProgramTemplateItem[]>([]);

  const totalItems = items.length;
  const currentItemType = items[currentIndex]?.template?.type as CreatableTouchpointType;
  const isEmail = currentItemType === TouchpointType.EMAIL;
  const touchpointLabel = getActionTouchpointTypeLabel(currentItemType);

  const [submitState, setSubmitState] = useState<LoadingButtonState>(LoadingButtonState.Default);
  const [createdTouchpoints, setCreatedTouchpoints] = useState<CreatedTouchpointSummary[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignDTO[]>([]);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignDTO | null>(null);

  const { data: contentTypes = [] } = useGetTouchpointContentTypesQuery(
    selectedCampaign?.campaignObjective,
  );

  const { classTypes, classTypeOptions } = useClassTypeOptions(selectedCampaign?.campaignObjective);
  const { populationTypeOptions } = usePopulationTypeOptions();

  const campaignOptions: OptionType[] = useMemo(
    () =>
      campaigns.map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [campaigns],
  );

  const contentTypeOptions: OptionType[] = useMemo(() => {
    if (!contentTypes) return [];
    return contentTypes.map((type) => ({
      label: type.description,
      value: type.name,
    }));
  }, [contentTypes]);

  const methods = useForm<CreateTouchpointFromProgramTemplateFormData>({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
    resolver: getCreateTouchpointFromProgramTemplateValidationSchema(currentItemType),
  });

  const {
    reset,
    watch,
    setValue,
    getValues,
    formState: { isValid },
  } = methods;

  const selectedCampaignId = watch("campaignId");
  const watchedForm = watch();

  // Callers often pass a new `selectedTemplates` array each render (e.g. `[template]`).
  // Depend on stable template ids so we don't wipe in-progress form state.
  const selectedTemplateIds = useMemo(
    () => selectedTemplates.map((template) => template.id).join(","),
    [selectedTemplates],
  );

  useEffect(() => {
    if (!isOpen || selectedTemplates.length === 0) {
      return;
    }

    const newItems: CreateTouchpointFromProgramTemplateItem[] = selectedTemplates.map(
      (template) => ({
        template,
        formData: {
          ...DEFAULT_VALUES,
          name: `Copy of ${template.name}`,
        },
      }),
    );
    setItems(newItems);
    setCurrentIndex(0);
    reset(newItems[0].formData);
    // selectedTemplates is read for names/types; identity is tracked via selectedTemplateIds
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedTemplateIds is the stable key
  }, [isOpen, selectedTemplateIds, reset]);

  const goNext = useCallback(() => {
    if (currentIndex >= totalItems - 1) return;
    const currentValues = getValues();
    const nextIndex = currentIndex + 1;
    const nextItems = items.map((item, index) =>
      index === currentIndex ? { ...item, formData: currentValues } : item,
    );
    setItems(nextItems);
    setCurrentIndex(nextIndex);
    reset(nextItems[nextIndex].formData);
  }, [currentIndex, totalItems, getValues, items, reset]);

  const goPrev = useCallback(() => {
    if (currentIndex <= 0) return;
    const currentValues = getValues();
    const prevIndex = currentIndex - 1;
    const nextItems = items.map((item, index) =>
      index === currentIndex ? { ...item, formData: currentValues } : item,
    );
    setItems(nextItems);
    setCurrentIndex(prevIndex);
    reset(nextItems[prevIndex].formData);
  }, [currentIndex, getValues, items, reset]);

  const isAllValid = useMemo(() => {
    if (items.length === 0) return false;
    return items.every((item, i) => {
      const schema = getCreateTouchpointFromProgramTemplateSchema(
        item.template.type as CreatableTouchpointType,
      );
      const dataToValidate = i === currentIndex ? watchedForm : item.formData;
      return schema.safeParse(dataToValidate).success;
    });
  }, [items, currentIndex, watchedForm]);

  useEffect(() => {
    if (!isOpen || !currentClient?.id) return;

    const fetchCampaigns = async () => {
      setIsCampaignsLoading(true);
      try {
        const campaignApi = getCampaign();
        const result = await campaignApi.getAllCampaignsForClient(
          { clientId: currentClient.id },
          { sort: ["name,asc"], unpaged: true },
        );
        setCampaigns(result.content || []);
      } catch (error) {
        console.error("Failed to fetch campaigns:", error);
        toast.error({
          data: {
            title: "Failed to load campaigns",
          },
        });
      } finally {
        setIsCampaignsLoading(false);
      }
    };

    fetchCampaigns();
  }, [isOpen, currentClient?.id]);

  useEffect(() => {
    if (!selectedCampaignId) {
      setSelectedCampaign(null);
      return;
    }

    const campaign = campaigns.find((c) => c.id === selectedCampaignId);
    if (campaign) {
      setSelectedCampaign(campaign);
    }
  }, [selectedCampaignId, campaigns]);

  useEffect(() => {
    if (!selectedCampaign || !contentTypes) return;

    if (contentTypes.length === 1) {
      setValue("contentType", contentTypes[0].name, { shouldValidate: true });
    } else if (contentTypes.length > 1) {
      const currentContentType = watch("contentType");
      const isCurrentValid = contentTypes.some((ct) => ct.name === currentContentType);
      if (!isCurrentValid) {
        setValue("contentType", "", { shouldValidate: false });
      }
    }
  }, [selectedCampaign, contentTypes, setValue, watch]);

  useEffect(() => {
    if (!classTypes.length) return;

    const currentClasses = getValues("classes") ?? [];
    const filteredClasses = filterTouchpointClassesByAllowedTypes(currentClasses, classTypes);
    const hasChanged =
      filteredClasses.length !== currentClasses.length ||
      filteredClasses.some((classType, index) => classType !== currentClasses[index]);

    // Only update when campaign class options invalidate a selection — avoid
    // setValue(..., shouldValidate) on an unchanged empty field (premature error).
    if (hasChanged) {
      setValue("classes", filteredClasses, { shouldValidate: true });
    }
  }, [classTypes, getValues, selectedCampaign?.id, setValue]);

  useEffect(() => {
    if (!isOpen) {
      reset(DEFAULT_VALUES);
      setSelectedCampaign(null);
      setSubmitState(LoadingButtonState.Default);
      setCreatedTouchpoints([]);
    }
  }, [isOpen, reset]);

  const handleCancelClick = useCallback(() => {
    if (createdTouchpoints.length > 0) {
      onSuccess?.();
    }
    onClose();
  }, [onClose, onSuccess, createdTouchpoints]);

  const handleSubmit = useCallback(
    async (_data: CreateTouchpointFromProgramTemplateFormData) => {
      setSubmitState(LoadingButtonState.Loading);
      try {
        const currentValues = getValues();
        const itemsForSubmit: CreateTouchpointFromProgramTemplateItem[] = items.map((item, i) =>
          i === currentIndex ? { ...item, formData: currentValues } : item,
        );

        const payload: CreateTouchpointFromTemplateItemRequest[] = itemsForSubmit.map(
          ({ template, formData }) => {
            return {
              // selectedTemplates come from the approved-templates table, which always pairs a
              // touchpoint with its latest APPROVED/PUBLISHED version
              templateTouchpointVersionId: template.latestVersion!.id,
              campaignId: formData.campaignId,
              name: formData.name,
              // isAllValid gates the submit button, so every item's fiscalYear has passed the
              // resolver's required check by the time this runs.
              fiscalYear: formData.fiscalYear!,
              copyCode: formData.copyCode,
              insightsActivator: formData.insightsActivator,
              populationType:
                formData.populationType as CreateTouchpointFromTemplateItemRequestPopulationType,
              contentType:
                formData.contentType as CreateTouchpointFromTemplateItemRequestContentType,
              classes: formatTouchpointClassesForApi(formData.classes),
            };
          },
        );

        const programTemplatesApi = getProgramTemplates();
        const results = await programTemplatesApi.touchpointsFromTemplates(
          { clientId: currentClient.id! },
          { items: payload },
        );

        const summaries: CreatedTouchpointSummary[] = results.map((tp) => {
          const campaignName = campaigns.find((c) => c.id === tp.campaignId)?.name ?? "";
          const { touchpointPreviewPath } = getTouchpointPaths({
            clientId: currentClient.id,
            campaignId: tp.campaignId,
            touchpointId: tp.id,
          });
          return {
            id: tp.id,
            name: tp.name,
            campaignId: tp.campaignId,
            campaignName,
            folderName: tp.folderName,
            type: tp.type,
            link: touchpointPreviewPath,
          };
        });

        setCreatedTouchpoints(summaries);
        setSubmitState(LoadingButtonState.Success);

        const count = results.length;
        toast.success({
          data: {
            title:
              count === 1
                ? `Created ${touchpointLabel} "${currentValues.name}"`
                : `Created ${count} creatives`,
          },
        });
      } catch (err: unknown) {
        const error = err as { response?: { data?: unknown } };
        toast.error({
          data: {
            title: isMulti
              ? "Failed to create creatives"
              : `Failed to create ${touchpointLabel.toLowerCase()}`,
            message: getErrorMessage(error?.response?.data),
          },
        });
        setSubmitState(LoadingButtonState.Default);
      }
    },
    [items, currentIndex, getValues, currentClient.id, campaigns, isMulti, touchpointLabel],
  );

  return {
    methods,
    isFormValid: isValid,
    submitState,
    handleSubmit,
    handleCancelClick,
    setSelectedCampaign,
    // Multi mode
    isMulti,
    currentIndex,
    totalItems,
    goNext,
    goPrev,
    isAllValid,
    // Data for selects
    campaignOptions,
    isCampaignsLoading,
    contentTypeOptions,
    contentTypes,
    classTypeOptions,
    populationTypeOptions,
    campaigns,
    // Type-specific helpers
    isEmail,
    touchpointLabel,
    // Summary screen
    createdTouchpoints,
  };
};
